import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Option } from '@sapphire/result';
import {
  distinctUntilChanged,
  EMPTY,
  exhaustMap,
  filter,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  tap,
} from 'rxjs';

import { EffectsBase, ReferenceDataFacade } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { SurveyService } from '@ps/survey-data-access';
import {
  DRAFT_SURVEY_DELETED_MESSAGE,
  DRAFT_SURVEYS_DELETED_MESSAGE,
  localeLabel,
  MC_TRADITIONAL_MESSAGES,
  McTemplateSurveys,
  SurveyKind,
} from '@ps/survey-utils';
import {
  TemplateApiActions,
  TemplateService,
} from '@ps/survey-template-data-access';

import {
  SurveyCreationAudienceActions,
  SurveyCreationAudienceApiActions,
} from './survey-creation-audience.actions';
import { SurveyCreationCoreApiActions } from '../core/survey-creation-core.actions';

@Injectable()
export class SurveyCreationMcTemplateEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _templateService = inject(TemplateService);
  private readonly _surveyService = inject(SurveyService);
  private readonly _referenceData = inject(ReferenceDataFacade);
  private readonly _toastService = inject(ToastService);

  public readonly createMcSurveysFromTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.createMcSurveysFromTemplate),
      exhaustMap(({ mcTemplateId, deleteSurveyIds }) =>
        this._templateService.createMcTemplateSurveys(mcTemplateId).pipe(
          map((created) => this._surveysCreated(created, deleteSurveyIds)),
          this.psCatchError(
            (error) =>
              of(
                SurveyCreationAudienceApiActions.createMcSurveysFromTemplateFailure(
                  { error: error.message },
                ),
              ),
            { notify: true },
          ),
        ),
      ),
    ),
  );

  // The surveys the template replaces go only once the new group exists, so a
  // failed create never loses them.
  public readonly deleteReplacedSurveys$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          SurveyCreationAudienceApiActions.createMcSurveysFromTemplateSuccess,
        ),
        filter(({ replacedSurveyIds }) => replacedSurveyIds.length > 0),
        mergeMap(({ replacedSurveyIds }) =>
          forkJoin(
            replacedSurveyIds.map((surveyId) => this._deleted(surveyId)),
          ).pipe(
            tap((outcomes) =>
              this._announceDeleted(outcomes.filter(Boolean).length),
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  // An MC template is listed once, under one of its locales' template ids, while
  // each Traditional MC locale stores its own; reading the locale's template
  // tells the list which MC template it belongs to.
  public readonly resolveLocaleTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationCoreApiActions.loadRootSurveyForEditSuccess,
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
      ),
      map(({ survey }) => survey),
      filter((survey) => SurveyKind.isTraditionalMc(survey)),
      map(({ templateId }) => Option.from(templateId)),
      filter((templateId) => templateId.isSome()),
      map((templateId) => templateId.unwrap()),
      distinctUntilChanged(),
      mergeMap((templateId) =>
        this._templateService.mcTemplateIdOf(templateId).pipe(
          mergeMap((mcTemplateId) =>
            mcTemplateId.mapOr(EMPTY, (resolved) =>
              of(
                TemplateApiActions.localeTemplateResolved({
                  templateId,
                  mcTemplateId: resolved,
                }),
              ),
            ),
          ),
          this.psCatchError(() => EMPTY, { metadata: { notify: false } }),
        ),
      ),
    ),
  );

  // Locales the backend could not create are reported; the first survey that
  // was created still opens.
  private _surveysCreated(
    { surveyIds, failedLocales }: McTemplateSurveys,
    replacedSurveyIds: number[],
  ): Action {
    this._reportFailedLocales(failedLocales);

    return Option.from(surveyIds[0]).match({
      some: (surveyId) => {
        this._toastService.success(
          MC_TRADITIONAL_MESSAGES.TEMPLATE_SURVEYS_ADDED,
          { keepOpen: false },
        );
        return SurveyCreationAudienceApiActions.createMcSurveysFromTemplateSuccess(
          { surveyId, replacedSurveyIds },
        );
      },
      none: () =>
        SurveyCreationAudienceApiActions.createMcSurveysFromTemplateFailure({
          error: MC_TRADITIONAL_MESSAGES.REJECTED_SURVEYS,
        }),
    });
  }

  private _reportFailedLocales(failedLocales: readonly string[]): void {
    if (!failedLocales.length) {
      return;
    }

    const labels = failedLocales.map((locale) =>
      localeLabel(locale, this._referenceData.countries()),
    );
    this._toastService.error(
      `${MC_TRADITIONAL_MESSAGES.REJECTED_SURVEYS} ${labels.join(', ')}`,
    );
  }

  // Each delete fails on its own, so one refused survey does not stop the rest.
  private _deleted(surveyId: number): Observable<boolean> {
    return this._surveyService.deleteSurvey(surveyId).pipe(
      map(() => true),
      this.psCatchError(() => of(false), { notify: true }),
    );
  }

  private _announceDeleted(deletedCount: number): void {
    if (!deletedCount) {
      return;
    }

    this._toastService.success(
      deletedCount === 1
        ? DRAFT_SURVEY_DELETED_MESSAGE
        : DRAFT_SURVEYS_DELETED_MESSAGE,
      { keepOpen: false },
    );
  }
}
